import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { draftsApi } from "../../apis/drafts";
import { usePermissions } from "../../hooks/usePermissions";
import { validateDraft } from "./snapshot";
import { Layers, Maximize2, Trash2, ChevronLeft, ChevronRight, RefreshCw, X, LockKeyhole, FileText, Inbox, AlertCircle, Loader2 } from "lucide-react";
import DraftIconButton from "./DraftIconButton";
import "./drafts.css";

const DraftsContext = createContext(null);
export const useDrafts = () => useContext(DraftsContext);
const metadataOnly = ({ id, title, transaction_code, revision }) => ({ id, title, transaction_code, revision });

function DraftRow({ draft, open, remove, busy }) {
  const rights = usePermissions(draft.transaction_code);
  return <li className="draft-row">
    <div className="draft-row-icon" aria-hidden="true"><FileText size={19} /></div>
    <span className="draft-row-info"><strong title={draft.title || "Untitled draft"}>{draft.title || "Untitled draft"}</strong><small><span className="draft-code-badge">{draft.transaction_code}</span> Revision {draft.revision}</small></span>
    <div className="draft-row-actions">
      <DraftIconButton icon={Maximize2} label="Restore" tooltip={rights.can_add ? `Restore ${draft.title || "draft"}` : "Add permission is required to restore"} disabled={!rights.can_add || busy} onClick={() => open(draft)} />
      <DraftIconButton icon={Trash2} className="draft-icon-button-danger" label={`Delete ${draft.title || "draft"}`} disabled={busy} onClick={() => remove(draft)} />
    </div>
  </li>;
}

export function DraftsProvider({ children, sidebarMinimized = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(null);
  const active = useRef(null);
  const request = useRef(0);
  const consumed = useRef(new Set());
  const interaction = useRef(false);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && expanded) setExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expanded]);
  const refresh = useCallback(async () => {
    const ticket = ++request.current;
    setLoading(true);
    try {
      const result = await draftsApi.list(page, 20);
      if (ticket !== request.current) return;
      if (!Array.isArray(result.data)) throw new Error("Unable to read the draft list. Please refresh to try again.");
      // Keep only list metadata, even if the API includes payloads.
      setItems(result.data.filter((d) => !d.submitted_tran_id && !consumed.current.has(d.id)).map(metadataOnly));
      setTotal(result.total || 0);
      setError("");
    } catch (e) { if (ticket === request.current) setError(e.message); }
    finally { if (ticket === request.current) setLoading(false); }
  }, [page]);
  useEffect(() => { refresh(); return () => { request.current += 1; }; }, [refresh]);
  const showMinimized = useCallback((draft) => {
    // The save response is confirmed server metadata. Show it immediately, even
    // if the follow-up list request fails; never keep its payload in the dock.
    request.current += 1;
    setMinimized(metadataOnly(draft));
    setItems((old) => [metadataOnly(draft), ...(page === 1 ? old.filter((d) => d.id !== draft.id) : [])].slice(0, 20));
    setTotal((old) => Math.max(old, 1));
    setError("");
    setPage(1);
    if (page === 1) refresh(); // A changed page triggers the effect instead.
  }, [page, refresh]);
  const removeCommitted = useCallback((id) => {
    consumed.current.add(id);
    setMinimized((old) => old?.id === id ? null : old);
    setItems((old) => old.filter((d) => d.id !== id));
    refresh(); // Consumption is atomic on insert. Never DELETE a consumed draft.
  }, [refresh]);
  const open = async (metadata) => {
    if (interaction.current) return;
    if (active.current?.blocked()) {
      setError("Wait for the current save or submission to finish.");
      return;
    }
    if (active.current?.dirty() && !window.confirm("This entry has unsaved changes. Cancel and Minimize first to save them, or continue and discard them?")) return;
    interaction.current = true;
    setBusy(true);
    try {
      const draft = await draftsApi.get(metadata.id);
      const { entry } = validateDraft(draft, metadata.transaction_code);
      if (active.current?.blocked()) throw new Error("A save or submission started. Wait for it to finish before restoring another draft.");
      // Navigate with ID only. The entry page independently fetches and validates it.
      navigate(`${entry.path}?draft=${encodeURIComponent(draft.id)}`);
      setMinimized(null);
      setExpanded(false);
      setError("");
    } catch (e) { setError(e.message); } finally { interaction.current = false; setBusy(false); }
  };
  const remove = async (draft) => {
    if (interaction.current) return;
    if (active.current?.id() === draft.id) { setError("Leave the active draft before deleting it."); return; }
    if (!window.confirm("Permanently delete this private draft?")) return;
    interaction.current = true;
    setBusy(true);
    try {
      await draftsApi.remove(draft.id, draft.revision);
      setMinimized((old) => old?.id === draft.id ? null : old);
      await refresh();
    }
    catch (e) { setError(e.message); } finally { interaction.current = false; setBusy(false); }
  };
  return <DraftsContext.Provider value={{ refresh, showMinimized, removeCommitted, active }}>
    {children}
    <aside className={`draft-dock ${sidebarMinimized ? "draft-dock-sidebar-minimized" : ""}`} aria-label="Private server drafts">
      <button type="button" className={`draft-dock-toggle ${expanded ? "is-expanded" : ""}`} aria-label={`Saved drafts (${total})${error ? " · Needs attention" : ""}`} title="Saved drafts" aria-expanded={expanded} aria-controls="minimized-transactions-panel" onClick={() => { setExpanded(!expanded); if (!expanded) refresh(); }}>
        <Layers size={23} strokeWidth={1.8} aria-hidden="true" />
        <span className="draft-count-badge" aria-hidden="true">{total > 99 ? "99+" : total}</span>
        {error && <span className="draft-dock-error-dot" aria-hidden="true" />}
      </button>
      {expanded && <div className="draft-dock-panel" id="minimized-transactions-panel">
        <header className="draft-panel-header">
          <div><h2>Saved workspace</h2><p><LockKeyhole size={12} aria-hidden="true" />Only visible to you</p></div>
          <div className="draft-panel-actions">
            <DraftIconButton icon={RefreshCw} label="Refresh" spinning={loading} disabled={busy || loading} onClick={refresh} />
            <DraftIconButton icon={X} label="Collapse draft panel" onClick={() => setExpanded(false)} />
          </div>
        </header>
        <div className="draft-panel-body">
          {error && <p className="draft-alert" role="alert"><AlertCircle size={16} aria-hidden="true" />{error}</p>}
          {loading && <p className="draft-loading" role="status"><Loader2 size={16} className="draft-spin" aria-hidden="true" />Loading saved transactions…</p>}
          {minimized && !items.some((draft) => draft.id === minimized.id) && <section className="draft-recent" aria-label="Just minimized transaction">
            <p>Just minimized</p>
            <ul><DraftRow draft={minimized} open={open} remove={remove} busy={busy} /></ul>
          </section>}
          <ul>{items.map((draft) => <DraftRow key={draft.id} draft={draft} open={open} remove={remove} busy={busy} />)}</ul>
          {!loading && !error && !items.length && !minimized && <div className="draft-empty"><Inbox size={32} aria-hidden="true" /><p>No drafts on this page.</p><small>Use Save draft or Minimize on a new transaction to resume it here.</small></div>}
        </div>
        <nav aria-label="Draft pages">
          <DraftIconButton icon={ChevronLeft} label="Previous" tooltip="Previous page" disabled={page === 1 || busy || loading} onClick={() => setPage(page - 1)} />
          <span>Page {page}</span>
          <DraftIconButton icon={ChevronRight} label="Next" tooltip="Next page" disabled={page * 20 >= total || busy || loading} onClick={() => setPage(page + 1)} />
        </nav>
      </div>}
    </aside>
  </DraftsContext.Provider>;
}